import React, { useState, useEffect, useContext } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { format } from "date-fns";
import { Helmet } from "react-helmet";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import Chip from "@mui/material/Chip";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import Autocomplete from "@mui/material/Autocomplete";
import EditIcon from "@mui/icons-material/Edit";
import ShareIcon from "@mui/icons-material/Share";
import PostAddIcon from "@mui/icons-material/PostAdd";
import { db } from "../../firebaseConfig";
import AppLoader from "../../layout/Loading";
import NoRecordsFound from "../../layout/NoRecordsFound";
import { DestinationNames, CabTypes, REQ_STATUS_TAG } from "../../Constants";
import { devConsolelog, MainContext } from "../../Utility";

const emptyFormData = {
  agentId: "",
  agentName: "",
  destination: "",
  cabType: "",
  pax: "",
  noOfNights: "",
  pickUp: "",
  startDate: "",
  trackingId: "",
  otherInfo: "",
  minBudget: "",
  maxBudget: "",
  notes: "",
};

const AdminRequests = () => {
  const { userData } = useContext(MainContext);
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filters, setFilters] = useState({
    status: "",
    destination: "",
    cabType: "",
    startDateFrom: "",
    startDateTo: "",
  });
  const [showForm, setShowForm] = useState(false);
  const [editingReq, setEditingReq] = useState(null);
  const [formData, setFormData] = useState(emptyFormData);

  const fetchAllRequests = async () => {
    setLoading(true);
    try {
      let q = query(
        collection(db, "agentRequirements"),
        orderBy(sortBy, sortOrder)
      );

      const unsubscribe = onSnapshot(
        q,
        (snapshot) => {
          let data = snapshot.docs.map((doc) => ({
            ...doc.data(),
            id: doc.id,
          }));
          setRequests(data);
          setFilteredRequests(data);
          devConsolelog("admin reqs - ", data);
          setLoading(false);
        },
        (error) => {
          console.error("Error fetching requests:", error);
          setLoading(false);
        }
      );
      return unsubscribe;
    } catch (error) {
      console.error("Error:", error);
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = fetchAllRequests();
    // return () => {
    //   if (unsubscribe) unsubscribe();
    // };
  }, [sortBy, sortOrder]);

  useEffect(() => {
    let filtered = [...requests];

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(
        (req) =>
          req.trackingId?.toLowerCase().includes(q) ||
          req.destination?.toLowerCase().includes(q) ||
          req.agentId?.toLowerCase().includes(q) ||
          req.agentName?.toLowerCase().includes(q)
      );
    }

    if (filters.status) {
      filtered = filtered.filter((req) => req.status === filters.status);
    }

    if (filters.destination) {
      filtered = filtered.filter((req) => req.destination === filters.destination);
    }

    if (filters.cabType) {
      filtered = filtered.filter((req) => req.cabType === filters.cabType);
    }

    if (filters.startDateFrom) {
      const fromDate = new Date(filters.startDateFrom).getTime();
      filtered = filtered.filter((req) => req.startDate >= fromDate);
    }

    if (filters.startDateTo) {
      const toDate = new Date(filters.startDateTo).getTime();
      filtered = filtered.filter((req) => req.startDate <= toDate);
    }

    setFilteredRequests(filtered);
  }, [requests, searchQuery, filters]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleSortChange = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const openAddForm = () => {
    setEditingReq(null);
    setFormData(emptyFormData);
    setShowForm(true);
  };

  const openEditForm = (req) => {
    setEditingReq(req);
    setFormData({
      agentId: req.agentId || "",
      agentName: req.agentName || "",
      destination: req.destination || "",
      cabType: req.cabType || "",
      pax: req.pax || "",
      noOfNights: req.noOfNights || "",
      pickUp: req.pickUp || "",
      startDate: req.startDate ? format(new Date(req.startDate), "yyyy-MM-dd") : "",
      trackingId: req.trackingId || "",
      otherInfo: req.otherInfo || "",
      minBudget: req.minBudget || "",
      maxBudget: req.maxBudget || "",
      notes: req.notes || "",
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingReq(null);
    setFormData(emptyFormData);
  };

  const handleSubmit = async () => {
    if (!formData.destination || !formData.startDate || !formData.agentId) {
      alert("Please fill required fields: Agent ID, Destination, Start Date");
      return;
    }

    try {
      const reqData = {
        ...formData,
        status: editingReq?.status || "active",
        startDate: new Date(formData.startDate).getTime(),
        otherInfo: formData.otherInfo || "",
      };

      if (editingReq?.id) {
        const docRef = doc(db, "agentRequirements", editingReq.id);
        await updateDoc(docRef, reqData);
      } else {
        const newReqId = `R-${Date.now()}`;
        await addDoc(collection(db, "agentRequirements"), {
          ...reqData,
          createdAt: Date.now(),
          reqId: newReqId,
        });
      }
      closeForm();
    } catch (error) {
      console.error("Error saving request:", error);
    }
  };

  const handleShare = (req) => {
    const shareUrl = `${window.location.origin}/req/view-bids/${req.id || req.reqId}`;
    if (navigator.share) {
      navigator.share({
        title: `Request ${req.trackingId || req.reqId}`,
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert("Link copied to clipboard!");
    }
  };

  const getStatusChipColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "expired":
        return "default";
      case "completed":
        return "primary";
      case "cancelled":
        return "error";
      default:
        return "default";
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return "-";
    try {
      return format(new Date(timestamp), "dd MMM yyyy");
    } catch {
      return "-";
    }
  };

  const RequestForm = () => (
    <Paper sx={{ p: 3, mb: 3 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        {editingReq ? "Edit Request" : "Add New Request"}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Agent ID (Phone) *"
            value={formData.agentId}
            onChange={(e) => setFormData({ ...formData, agentId: e.target.value })}
            placeholder="91XXXXXXXX"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Agent Name"
            value={formData.agentName}
            onChange={(e) => setFormData({ ...formData, agentName: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Destination *</InputLabel>
            <Select
              value={formData.destination}
              label="Destination *"
              onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
            >
              {DestinationNames.map((dest) => (
                <MenuItem key={dest} value={dest}>{dest}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            disablePortal
            freeSolo
            options={CabTypes}
            value={formData.cabType}
            onChange={(e, val) => setFormData({ ...formData, cabType: val || "" })}
            renderInput={(params) => <TextField {...params} label="Cab Type" />}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="Pax"
            value={formData.pax}
            onChange={(e) => setFormData({ ...formData, pax: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label="No of Nights"
            value={formData.noOfNights}
            onChange={(e) => setFormData({ ...formData, noOfNights: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Pick Up"
            value={formData.pickUp}
            onChange={(e) => setFormData({ ...formData, pickUp: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            type="date"
            label="Start Date *"
            InputLabelProps={{ shrink: true }}
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Lead Pax Name"
            value={formData.trackingId}
            onChange={(e) => setFormData({ ...formData, trackingId: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Min Budget"
            type="number"
            value={formData.minBudget}
            onChange={(e) => setFormData({ ...formData, minBudget: e.target.value })}
            InputProps={{ startAdornment: <InputAdornment position="start">INR</InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth
            label="Max Budget"
            type="number"
            value={formData.maxBudget}
            onChange={(e) => setFormData({ ...formData, maxBudget: e.target.value })}
            InputProps={{ startAdornment: <InputAdornment position="start">INR</InputAdornment> }}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Detailed Itinerary / Notes"
            value={formData.otherInfo}
            onChange={(e) => setFormData({ ...formData, otherInfo: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" onClick={handleSubmit} sx={{ mt: 1 }}>
            {editingReq ? "Update Request" : "Submit Request"}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );

  const FilterSection = () => (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
      <TextField
        sx={{ minWidth: 250, flex: 1 }}
        placeholder="Search by Tracking ID, Agent, Destination..."
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />
      <FormControl sx={{ minWidth: 120 }}>
        <InputLabel>Status</InputLabel>
        <Select
          value={filters.status}
          label="Status"
          onChange={(e) => handleFilterChange("status", e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {Object.keys(REQ_STATUS_TAG).map((status) => (
            <MenuItem key={status} value={status}>
              {REQ_STATUS_TAG[status].value}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 120 }}>
        <InputLabel>Destination</InputLabel>
        <Select
          value={filters.destination}
          label="Destination"
          onChange={(e) => handleFilterChange("destination", e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {DestinationNames.map((dest) => (
            <MenuItem key={dest} value={dest}>{dest}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl sx={{ minWidth: 120 }}>
        <InputLabel>Cab Type</InputLabel>
        <Select
          value={filters.cabType}
          label="Cab Type"
          onChange={(e) => handleFilterChange("cabType", e.target.value)}
        >
          <MenuItem value="">All</MenuItem>
          {CabTypes.map((type) => (
            <MenuItem key={type} value={type}>{type}</MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        type="date"
        label="From Date"
        InputLabelProps={{ shrink: true }}
        value={filters.startDateFrom}
        onChange={(e) => handleFilterChange("startDateFrom", e.target.value)}
        sx={{ minWidth: 150 }}
      />
      <TextField
        type="date"
        label="To Date"
        InputLabelProps={{ shrink: true }}
        value={filters.startDateTo}
        onChange={(e) => handleFilterChange("startDateTo", e.target.value)}
        sx={{ minWidth: 150 }}
      />
    </Box>
  );

  return (
    <>
      <Helmet>
        <title>Admin - Requests Management</title>
      </Helmet>

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6}>
            <Typography variant="h5" fontWeight={600}>
              Requests Management
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} sx={{ textAlign: "right" }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={showForm ? closeForm : openAddForm}
            >
              {showForm ? "Cancel" : "Add New Request"}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {showForm && <RequestForm />}

      <Paper sx={{ p: 2, mb: 3 }}>
        <FilterSection />
      </Paper>

      <Box sx={{ mb: 2, display: "flex", gap: 1, alignItems: "center" }}>
        <Typography variant="body2" color="text.secondary">
          Sort by:
        </Typography>
        <Button
          size="small"
          variant={sortBy === "createdAt" ? "contained" : "outlined"}
          onClick={() => handleSortChange("createdAt")}
        >
          Created {sortBy === "createdAt" && (sortOrder === "desc" ? "↓" : "↑")}
        </Button>
        <Button
          size="small"
          variant={sortBy === "startDate" ? "contained" : "outlined"}
          onClick={() => handleSortChange("startDate")}
        >
          Start Date {sortBy === "startDate" && (sortOrder === "desc" ? "↓" : "↑")}
        </Button>
      </Box>

      {loading ? (
        <AppLoader />
      ) : filteredRequests.length === 0 ? (
        <NoRecordsFound />
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Tracking ID</TableCell>
                <TableCell>Agent</TableCell>
                <TableCell>Destination</TableCell>
                <TableCell>Cab Type</TableCell>
                <TableCell>Pax</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredRequests.map((req) => (
                <TableRow key={req.id || req.reqId}>
                  <TableCell>{req.trackingId || req.reqId || "-"}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{req.agentName || req.agentId || "-"}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {req.agentId}
                    </Typography>
                  </TableCell>
                  <TableCell>{req.destination || "-"}</TableCell>
                  <TableCell>{req.cabType || "-"}</TableCell>
                  <TableCell>{req.pax || "-"}</TableCell>
                  <TableCell>{"-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={req.status || "active"}
                      color={getStatusChipColor(req.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon />}
                        onClick={() => openEditForm(req)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PostAddIcon />}
                        onClick={() => window.open(`/req/post-bid/${req.id}`, "_blank")}
                      >
                        Post Bid
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<ShareIcon />}
                        onClick={() => handleShare(req)}
                      >
                        Share
                      </Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
        Total Requests: {filteredRequests.length}
      </Typography>
    </>
  );
};

export default AdminRequests;